import { useCallback, useEffect, useState } from 'react'

import {
  listSupplierCustomers,
  type SupplierCustomersPageDto,
} from '../api/supplierCustomers'
import { useAuth } from '../auth/AuthProvider'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'

export const SupplierCustomersScreen = () => {
  const { session } = useAuth()
  const [pageIndex, setPageIndex] = useState(0)
  const [page, setPage] = useState<SupplierCustomersPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPage = useCallback(
    async (index: number) => {
      if (!session) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const result = await listSupplierCustomers(session.organizationId, index)
        setPage(result)
        setPageIndex(index)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить клиентов')
      } finally {
        setLoading(false)
      }
    },
    [session],
  )

  useEffect(() => {
    void loadPage(0)
  }, [loadPage])

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Мои клиенты"
        description="Рестораны, с которыми вы уже работали по заявкам."
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && page?.items.length === 0 ? (
        <MarketEmptyState
          title="Клиентов пока нет"
          description="Рестораны появятся здесь после первой принятой заявки."
          actions={[{ label: 'Входящие запросы', to: '/orders', variant: 'ghost' }]}
        />
      ) : null}

      {page?.items.map((customer) => (
        <article key={customer.restaurantOrganizationId} className="echat-panel market-card-list-item">
          <h2 className="echat-panel__subtitle">{customer.restaurantName}</h2>
          {customer.email ? <p className="echat-panel__text">Email: {customer.email}</p> : null}
          <p className="echat-muted">Заказов: {customer.ordersCount}</p>
          {customer.lastOrderedAt ? (
            <p className="echat-muted">Последний заказ: {customer.lastOrderedAt}</p>
          ) : null}
        </article>
      ))}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0}
            onClick={() => void loadPage(pageIndex - 1)}
          >
            ←
          </button>
          <span>
            Страница {page.page + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1}
            onClick={() => void loadPage(pageIndex + 1)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
